import axios from "axios";
import { isNative, getAccessToken, getRefreshToken, setTokens, clearTokens } from "@/lib/native";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const native = isNative();

// Web uses httpOnly cookies (same-origin). The native app uses Bearer tokens in secure
// storage, because cross-origin cookies don't work from the Capacitor webview.
const api = axios.create({ baseURL: API, withCredentials: !native });

if (native) {
  api.interceptors.request.use(async (config) => {
    const token = await getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
}

const AUTH_PATHS = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/logout"];
let refreshPromise = null;

async function doRefresh() {
  if (native) {
    const rt = await getRefreshToken();
    const { data } = await api.post("/auth/refresh", {}, { headers: rt ? { "X-Refresh-Token": rt } : {} });
    if (data?.token) await setTokens(data.token, data.refresh_token);
    return data;
  }
  return (await api.post("/auth/refresh")).data; // web: cookie-based refresh
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const isAuthPath = original && AUTH_PATHS.some((p) => (original.url || "").includes(p));
    const skipRefresh = original && original.skipAuthRefresh;
    if (status === 401 && original && !original._retry && !isAuthPath && !skipRefresh) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = doRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        await refreshPromise;
        if (native) {
          const t = await getAccessToken();
          if (t) original.headers.Authorization = `Bearer ${t}`;
        }
        return api(original);
      } catch (e) {
        if (native) await clearTokens();
        window.dispatchEvent(new Event("auth:expired"));
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
